import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Minus,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Presentation as PresentationIcon,
  X,
} from "lucide-react";
import type { VerifiedEvent } from "nostr-tools/pure";
import { TreeInspector } from "@/components/TreeInspector";
import { useHashtreeBuild } from "@/hashtree/useHashtreeBuild";
import { isTypingTarget, usePresentation } from "@/hashtree/usePresentation";
import { cn } from "@/lib/utils";
import { ChunkSizeSlide } from "./slides/ChunkSizeSlide";
import { FolderSlide, RootSlide } from "./slides/DirectorySlide";
import { DoneSlide } from "./slides/DoneSlide";
import { EncryptSlide } from "./slides/EncryptSlide";
import { FileManifestSlide } from "./slides/FileManifestSlide";
import { IntroSlide } from "./slides/IntroSlide";
import { PickFileSlide } from "./slides/PickFileSlide";
import { PublishSlide } from "./slides/PublishSlide";
import { UploadSlide } from "./slides/UploadSlide";
import "./index.css";

const STEPS = [
  { title: "Intro", bud: null },
  { title: "File", bud: null },
  { title: "Chunks", bud: 17 },
  { title: "Encrypt", bud: 15 },
  { title: "File node", bud: 17 },
  { title: "Folder", bud: 16 },
  { title: "Root", bud: 16 },
  { title: "Upload", bud: null },
  { title: "Publish", bud: 18 },
  { title: "Done", bud: null },
] as const;

const TREE_PANEL_KEY = "hashtree-explainer:show-tree";

export function App() {
  const build = useHashtreeBuild();
  const presentation = usePresentation();
  const [step, setStep] = useState(0);
  const [event, setEvent] = useState<VerifiedEvent | null>(null);
  const [linkKey, setLinkKey] = useState<Uint8Array | undefined>();

  // The tree panel is genuinely useful, but it is also the whole answer sitting
  // next to the question. Hiding it lets the walkthrough read as a walkthrough,
  // and the choice sticks so it does not have to be made on every slide.
  const [showTree, setShowTree] = useState(() => {
    try {
      return localStorage.getItem(TREE_PANEL_KEY) !== "false";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(TREE_PANEL_KEY, String(showTree));
    } catch {
      // Private browsing and the like — the panel simply will not remember.
    }
  }, [showTree]);

  // A step is only reachable once the thing it explains exists.
  const furthest = useMemo(() => {
    if (!build.file) return 1;
    if (!build.tree) return 2;
    return STEPS.length - 1;
  }, [build.file, build.tree]);

  const canAdvance = step < furthest && step < STEPS.length - 1;
  const next = useCallback(() => setStep((current) => Math.min(current + 1, STEPS.length - 1)), []);
  const back = useCallback(() => setStep((current) => Math.max(current - 1, 0)), []);

  // Presenting from across a room means driving it with a clicker, which sends
  // arrow keys and page up/down.
  useEffect(() => {
    if (!presentation.active) return;

    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (isTypingTarget(keyEvent.target)) return;

      if (keyEvent.key === "ArrowRight" || keyEvent.key === "PageDown" || keyEvent.key === " ") {
        if (canAdvance) {
          keyEvent.preventDefault();
          next();
        }
      } else if (keyEvent.key === "ArrowLeft" || keyEvent.key === "PageUp") {
        keyEvent.preventDefault();
        back();
      } else if (keyEvent.key === "Escape") {
        // Normally the browser leaves fullscreen and we follow it. If fullscreen
        // was refused, this is the only thing that gets us out.
        presentation.exit();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [presentation, canAdvance, next, back]);

  const restart = () => {
    build.setFile(null);
    build.regenerateIdentity();
    setEvent(null);
    setLinkKey(undefined);
    setStep(0);
  };

  const presenting = presentation.active;
  // The tree panel is a distraction on a shared screen, so presenting implies
  // hiding it, without disturbing the user's own preference for later.
  const treeVisible = showTree && !presenting;

  return (
    <div className="min-h-screen w-full">
      {!presenting && (
        <header className="sticky top-0 z-10 border-b bg-background/85 backdrop-blur">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
            <h1 className="text-sm font-semibold tracking-tight">Hashtree explainer</h1>
            <p className="hidden text-xs text-muted-foreground sm:block">
              BUD-15 / 16 / 17 / 18 — build a directory tree out of Blossom blobs
            </p>

            <nav className="ml-auto flex flex-wrap items-center gap-1">
              {STEPS.map((entry, index) => {
                const reachable = index <= furthest;
                return (
                  <button
                    key={entry.title}
                    type="button"
                    disabled={!reachable}
                    onClick={() => setStep(index)}
                    title={entry.bud ? `${entry.title} — BUD-${entry.bud}` : entry.title}
                    className={cn(
                      "rounded px-2 py-1 text-xs transition-colors",
                      index === step
                        ? "bg-primary font-medium text-primary-foreground"
                        : reachable
                          ? "text-muted-foreground hover:bg-muted"
                          : "text-muted-foreground/40",
                    )}
                  >
                    {entry.title}
                  </button>
                );
              })}
            </nav>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setShowTree((value) => !value)}
                aria-pressed={showTree}
                title={showTree ? "Hide the tree panel" : "Show the tree panel"}
                className="inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {showTree ? <PanelRightClose className="size-3.5" /> : <PanelRightOpen className="size-3.5" />}
                <span className="hidden sm:inline">{showTree ? "Hide tree" : "Show tree"}</span>
              </button>

              <button
                type="button"
                onClick={presentation.enter}
                title="Fullscreen presentation mode"
                className="inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <PresentationIcon className="size-3.5" />
                <span className="hidden sm:inline">Present</span>
              </button>
            </div>
          </div>
        </header>
      )}

      <main
        className={cn(
          "mx-auto grid gap-8 px-5",
          presenting ? "max-w-5xl py-10 pb-24" : "py-8",
          // Without the panel the column would stretch to an unreadable width,
          // so narrow it rather than leaving the space empty.
          treeVisible ? "max-w-7xl lg:grid-cols-[minmax(0,1fr)_24rem]" : "grid-cols-1",
          !treeVisible && !presenting && "max-w-4xl",
        )}
      >
        <div className="min-w-0">
          {step === 0 && <IntroSlide onStart={next} />}
          {step === 1 && (
            <PickFileSlide
              file={build.file}
              onPick={(picked) => {
                build.setFile(picked);
                setStep(2);
              }}
              onNext={next}
            />
          )}
          {step === 2 && <ChunkSizeSlide build={build} onNext={next} />}
          {step === 3 && <EncryptSlide build={build} onNext={next} />}
          {step === 4 && <FileManifestSlide build={build} onNext={next} />}
          {step === 5 && <FolderSlide build={build} onNext={next} />}
          {step === 6 && <RootSlide build={build} onNext={next} />}
          {step === 7 && <UploadSlide build={build} onNext={next} />}
          {step === 8 && (
            <PublishSlide
              build={build}
              onPublished={(published, key) => {
                setEvent(published);
                setLinkKey(key);
              }}
              onNext={next}
            />
          )}
          {step === 9 && <DoneSlide build={build} event={event} linkKey={linkKey} onRestart={restart} />}

          {!presenting && (
            <div className="mt-8 flex items-center justify-between border-t pt-4">
              <button
                type="button"
                onClick={back}
                disabled={step === 0}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
              >
                <ChevronLeft className="size-4" /> Back
              </button>
              <span className="text-xs text-muted-foreground">
                {step + 1} / {STEPS.length}
              </span>
              <button
                type="button"
                onClick={next}
                disabled={!canAdvance}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
              >
                Next <ChevronRight className="size-4" />
              </button>
            </div>
          )}
        </div>

        {treeVisible && (
          <aside className="lg:sticky lg:top-20 lg:self-start">
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <h2 className="text-sm font-medium">Your tree</h2>
                {build.tree && (
                  <span className="text-xs text-muted-foreground">
                    {build.tree.blobs.length} blob{build.tree.blobs.length === 1 ? "" : "s"}
                    {build.stale && " · rebuilding"}
                  </span>
                )}
              </div>
              <TreeInspector
                root={build.tree?.root ?? null}
                emptyHint="Pick a file and your tree will grow here, node by node."
              />
              {build.buildError && <p className="text-xs text-red-600 dark:text-red-400">{build.buildError}</p>}
            </div>
          </aside>
        )}
      </main>

      {presenting && (
        <PresenterBar
          step={step}
          total={STEPS.length}
          title={STEPS[step]!.title}
          canAdvance={canAdvance}
          onBack={back}
          onNext={next}
          presentation={presentation}
        />
      )}
    </div>
  );
}

/**
 * The only chrome in presentation mode.
 *
 * It sits low and faint so it does not compete with the slide, and comes to
 * full opacity when the presenter reaches for it.
 */
function PresenterBar({
  step,
  total,
  title,
  canAdvance,
  onBack,
  onNext,
  presentation,
}: {
  step: number;
  total: number;
  title: string;
  canAdvance: boolean;
  onBack: () => void;
  onNext: () => void;
  presentation: ReturnType<typeof usePresentation>;
}) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-20 flex justify-center pb-4">
      <div className="flex items-center gap-1 rounded-full border bg-background/80 px-2 py-1.5 opacity-40 shadow-lg backdrop-blur transition-opacity hover:opacity-100 focus-within:opacity-100">
        <BarButton onClick={onBack} disabled={step === 0} label="Previous slide">
          <ChevronLeft className="size-4" />
        </BarButton>

        <span className="px-2 text-xs tabular-nums text-muted-foreground">
          {step + 1} / {total}
          <span className="ml-1.5 hidden font-medium text-foreground sm:inline">{title}</span>
        </span>

        <BarButton onClick={onNext} disabled={!canAdvance} label="Next slide">
          <ChevronRight className="size-4" />
        </BarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        <BarButton onClick={presentation.zoomOut} disabled={!presentation.canZoomOut} label="Smaller text">
          <Minus className="size-3.5" />
        </BarButton>
        <span className="w-10 text-center text-xs tabular-nums text-muted-foreground">{presentation.zoom}px</span>
        <BarButton onClick={presentation.zoomIn} disabled={!presentation.canZoomIn} label="Larger text">
          <Plus className="size-3.5" />
        </BarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        <BarButton onClick={presentation.exit} label="Leave presentation mode">
          <X className="size-4" />
        </BarButton>
      </div>
    </div>
  );
}

function BarButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}

export default App;
